import { ApolloError } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { userSettingsRouteConfig } from 'src/app/account/routes';
import { Loading } from 'src/components/common/loading/Loading';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { usePersonalSettingsQuery } from 'src/hooks/usePersonalSettingsQuery';
import { useRouteParams } from 'src/hooks/useRouteParams';

import { UserApiKeysCard } from './apiKeySettings/userApiKeysCard/UserApiKeysCard';
import { AvatarCard } from './generalSettings/avatarCardSettings/AvatarCardSettings';
import { BetaFeaturesCard } from './generalSettings/betaFeaturesCardSettings/BetaFeaturesCardSettings';
import { DeleteAccountCard } from './generalSettings/deleteUserCardSettings/DeleteUserCardSettings';
import { EmailCard } from './generalSettings/emailCardSettings/EmailCardSettings';
import { NameCard } from './generalSettings/nameCardSettings/NameCardSettings';
import { OrganizationsCard } from './generalSettings/organizationsCardSettings/OrganizationsCardSettings';
import { PasswordCard } from './generalSettings/passwordCardSettings/PasswordCardSettings';
import { UserSettingsMenu } from './UserSettingsMenu';

export function SettingsPageLayout() {
  const { loading, data, error } = usePersonalSettingsQuery();

  const [l3MenuWidth, setL3MenuWidth] = React.useState(275);
  const [actionError, setActionError] = useState<ApolloError | null>(null);
  const { tab = 'general' } = useRouteParams(userSettingsRouteConfig);

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="mb-4 text-3xl">Error loading settings</div>
          <div className="mb-4 text-placeholder">{error.message}</div>
        </div>
      </div>
    );
  }
  if (loading) {
    return <Loading />;
  }

  if (!data || !data.me || data.me.__typename !== 'User') {
    return (
      <div className="flex flex-1 items-center justify-center py-6">
        <div className="max-w-md text-center">
          <div className="mb-4 text-3xl">Error loading settings</div>
          <div className="mb-4 text-placeholder">
            There was a problem loading your settings data.
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <PageLayout
        title={<PageTitle title="Personal Settings" />}
        l3MenuProps={{
          minWidth: 140,
          maxWidth: 600,
          setWidth: setL3MenuWidth,
          width: l3MenuWidth,
        }}
        l3Menu={<UserSettingsMenu />}
      >
        <div className="grid w-full gap-4 py-4 pl-1 pr-4">
          {tab === 'general' || tab === undefined ? (
            <>
              <NameCard user={data.me} />

              <EmailCard user={data.me} />
              <AvatarCard />
              {!data.me.synchronized && <PasswordCard userId={data.me.id} />}
              <OrganizationsCard user={data.me} />
              <BetaFeaturesCard user={data.me} />
              <DeleteAccountCard user={data.me} />

              {actionError && (
                <Modal isOpen size="lg" onClose={() => setActionError(null)}>
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>
                      <ModalTitle>Error</ModalTitle>
                    </ModalHeader>
                    <ModalBody>
                      <div className="w-full rounded bg-red-lightest px-4 py-3 text-base leading-normal text-red-dark">
                        <div>An error occurred: {actionError.message}</div>
                        <div>Please try again</div>
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <Button onClick={() => setActionError(null)}>
                        Close
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
              )}
            </>
          ) : (
            tab === 'api-keys' && (
              <UserApiKeysCard user={data.me} setError={setActionError} />
            )
          )}
        </div>
      </PageLayout>
    );
  }
}
